/**
 * "New" markers for the sidebar.
 *
 * Answers one question per section: has anything landed since I last looked?
 * Not "how much have I not watched" — that's a backlog, and a badge that
 * describes a backlog is permanently on, which is the same as being off.
 *
 * The guardrails live in content_new_counts() (a hard 14-day floor, and no
 * badge at all for a section you've never opened). This hook owns the other
 * half: stamping a section as seen the moment you open it, and clearing the
 * badge optimistically so it never lingers on the screen you're looking at.
 *
 * The seen map lives in user_data, so it follows a coach across devices —
 * reading Insights on a laptop shouldn't leave the phone claiming it's new.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { loadUserData, saveUserData } from "../utils/userData";

const KEY = "section_last_seen";

// Only sections a coach browses. Toolbox pages and one-off surfaces don't
// accumulate content, so a badge there would never fire and would just be
// one more thing to maintain.
export const BADGED_SECTIONS = ["pd", "insights", "movement", "workouts", "team", "ti", "xo", "master"];

export function useNewContent(nav, enabled = true) {
  const [counts, setCounts] = useState({});
  // The seen map as it stood when this session started, so a page can ask
  // "what landed since my previous visit" even after it has been stamped.
  const [lastSeen, setLastSeen] = useState({});
  const seenRef = useRef(null);          // { section: iso } once loaded
  const stampedRef = useRef(new Set());  // sections stamped this session

  const refresh = useCallback(async (seen) => {
    try {
      const { data, error } = await supabase.rpc("content_new_counts", { p_since: seen || {} });
      if (!error && data) setCounts(data);
    } catch { /* a missing badge is not worth surfacing */ }
  }, []);

  // Load the seen map once, then ask what's new against it.
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    (async () => {
      const seen = await loadUserData(KEY, {});
      if (!live) return;
      seenRef.current = seen && typeof seen === "object" ? seen : {};
      setLastSeen({ ...seenRef.current });
      refresh(seenRef.current);
    })();
    return () => { live = false; };
  }, [enabled, refresh]);

  // Opening a section clears it. Optimistic locally so the badge disappears
  // as you arrive rather than a round trip later; the write is best-effort
  // because a failed stamp costs a stale badge, not data.
  useEffect(() => {
    if (!enabled || !nav) return;
    if (!BADGED_SECTIONS.includes(nav)) return;
    if (seenRef.current === null) return;      // not loaded yet
    if (stampedRef.current.has(nav)) return;   // already done this session

    stampedRef.current.add(nav);
    setCounts(c => (c[nav] ? { ...c, [nav]: 0 } : c));

    const next = { ...seenRef.current, [nav]: new Date().toISOString() };
    seenRef.current = next;
    saveUserData(KEY, next).catch(() => {});
  }, [nav, enabled]);

  return { counts, lastSeen };
}
